/**
 * Questo file definisce la pagina "Account".
 * - Consente agli utenti autenticati di visualizzare e gestire le proprie informazioni.
 * - È strutturata con una navigazione a schede (tab) per separare le diverse sezioni:
 * 1. Profilo: mostra i dati di base dell'utente (nome, email, ruolo).
 * 2. API Key: permette all'utente di inserire e salvare la propria API key di OpenAI.
 * 3. Sicurezza: offre un form per cambiare la password.
 * - Interagisce con lo store Redux per ottenere i dati dell'utente e per dispatchare
 * azioni asincrone per salvare la API key e cambiare la password.
 * - Gestisce lo stato locale per i campi dei form e per i messaggi di feedback (successo/errore).
 */

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Navbar from '../components/Navbar';
// Importa le azioni Redux necessarie.
import { saveApiKey, changePassword } from '../redux/authSlice';
import './Account.css';

export default function Account() {
    // Hook per dispatchare azioni Redux.
    const dispatch = useDispatch();
    // Hook per estrarre dati dallo store Redux. Ottiene i dati dell'utente loggato.
    const { user } = useSelector((state) => state.auth);
    
    // --- Stato per la gestione della API Key ---
    // Stato per il campo di input della API key.
    const [apiKey, setApiKey] = useState('');
    // Stato per mostrare messaggi di feedback (es. "Salvataggio riuscito!").
    const [apiKeyMessage, setApiKeyMessage] = useState(null);

    // --- Stato per la gestione del Cambio Password ---
    // Stato per i campi del form di cambio password.
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    // Stato per i messaggi di feedback relativi alla password.
    const [passwordMessage, setPasswordMessage] = useState(null);

    /**
     * Gestore per l'invio del form della API Key.
     * @param {React.FormEvent} e - L'evento del form.
     */
    const handleApiKeySubmit = (e) => {
        e.preventDefault(); // Previene il ricaricamento della pagina.
        setApiKeyMessage(null); // Pulisce i messaggi precedenti.
        // Fa il dispatch dell'azione asincrona `saveApiKey`.
        dispatch(saveApiKey({ api_key: apiKey }))
            .unwrap() // `.unwrap()` restituisce una Promise che viene risolta con il payload o rigettata con l'errore.
            .then(() => {
                // In caso di successo, mostra un messaggio di successo e pulisce il form.
                setApiKeyMessage({ type: 'success', text: 'API Key salvata con successo!' });
                setApiKey('');
            })
            .catch((err) => {
                // In caso di errore, mostra il messaggio di errore restituito dall'API.
                setApiKeyMessage({ type: 'danger', text: err || 'Si è verificato un errore.' });
            });
    };

    /**
     * Gestore per l'aggiornamento dei campi del form password.
     * @param {React.ChangeEvent} e - L'evento di cambio dell'input.
     */
    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    /**
     * Gestore per l'invio del form di cambio password.
     * @param {React.FormEvent} e - L'evento del form.
     */
    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        setPasswordMessage(null);

        // Validazione frontend: controlla che le nuove password coincidano.
        if (passwordData.new_password !== passwordData.confirm_password) {
            setPasswordMessage({ type: 'danger', text: 'Le nuove password non coincidono.' });
            return;

        }

        // Fa il dispatch dell'azione asincrona `changePassword`.
        dispatch(changePassword(passwordData))
            .unwrap()
            .then(() => {
                // Successo: mostra messaggio e pulisce il form.
                setPasswordMessage({ type: 'success', text: 'Password modificata con successo!' });
                setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
            })
            .catch((err) => {
                // Errore: mostra messaggio.
                setPasswordMessage({ type: 'danger', text: err || 'Si è verificato un errore.' });
            });
    };

    return (
        <>
            <Navbar />
            <div className="account-container">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <div className="account-header">
                                <h1 className="display-5 fw-bold">Il mio account</h1>
                                <p className="text-muted">Gestisci le tue informazioni personali e le impostazioni.</p>
                            </div>

                            <div className="account-content-card">
                                {/* Navigazione a schede (tabs) di Bootstrap */}
                                <ul className="nav nav-tabs" id="accountTabs" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link active" id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile-pane" type="button" role="tab">
                                            <i className="bi bi-person-circle me-2"></i>Profilo
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" id="api-key-tab" data-bs-toggle="tab" data-bs-target="#api-key-pane" type="button" role="tab">
                                            <i className="bi bi-key-fill me-2"></i>API Key
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" id="security-tab" data-bs-toggle="tab" data-bs-target="#security-pane" type="button" role="tab">
                                            <i className="bi bi-shield-lock-fill me-2"></i>Sicurezza
                                        </button>
                                    </li>
                                </ul>
                                <div className="tab-content p-4">
                                    {/* Pannello 1: Dati Profilo */}
                                    <div className="tab-pane fade show active" id="profile-pane" role="tabpanel">
                                        <h4 className="mb-4">Dati utente</h4>
                                        <ul className="list-group list-group-flush profile-details-list">
                                            <li className="list-group-item">
                                                <i className="bi bi-person-badge-fill text-muted"></i>
                                                <div><strong>Nome:</strong><span>{user?.nome}</span></div>
                                            </li>
                                            <li className="list-group-item">
                                                <i className="bi bi-envelope-fill text-muted"></i>
                                                <div><strong>Email:</strong><span>{user?.email}</span></div>
                                            </li>
                                            <li className="list-group-item">
                                                <i className="bi bi-shield-check text-muted"></i>
                                                <div><strong>Ruolo:</strong><span className="badge">{user?.ruolo}</span></div>
                                            </li>
                                        </ul>
                                    </div>

                                    {/* Pannello 2: API Key */}
                                    <div className="tab-pane fade" id="api-key-pane" role="tabpanel">
                                        <h4 className="mb-3">Imposta la tua API Key di OpenAI</h4>
                                        <p className="text-muted small mb-4">La tua API key è necessaria per generare le flashcard. Verrà salvata in modo sicuro e non sarà mai visibile pubblicamente.</p>
                                        {apiKeyMessage && (<div className={`alert alert-${apiKeyMessage.type}`}>{apiKeyMessage.text}</div>)}
                                        <form onSubmit={handleApiKeySubmit}>
                                            <div className="mb-3">
                                                <label htmlFor="apiKey" className="form-label">API Key</label>
                                                <input type="password" className="form-control" id="apiKey" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-..." required />
                                            </div>
                                            <button type="submit" className="btn btn-primary">Salva API Key</button>
                                        </form>
                                    </div>

                                    {/* Pannello 3: Sicurezza (Cambio Password) */}
                                    <div className="tab-pane fade" id="security-pane" role="tabpanel">
                                        <h4 className="mb-3">Modifica password</h4>
                                        {passwordMessage && (<div className={`alert alert-${passwordMessage.type}`}>{passwordMessage.text}</div>)}
                                        <form onSubmit={handlePasswordSubmit}>
                                            <div className="mb-3">
                                                <label htmlFor="current_password" className="form-label">Password corrente</label>
                                                <input type="password" name="current_password" className="form-control" id="current_password" value={passwordData.current_password} onChange={handlePasswordChange} required />
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="new_password" className="form-label">Nuova password</label>
                                                <input type="password" name="new_password" className="form-control" id="new_password" value={passwordData.new_password} onChange={handlePasswordChange} required />
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="confirm_password" className="form-label">Conferma nuova password</label>
                                                <input type="password" name="confirm_password" className="form-control" id="confirm_password" value={passwordData.confirm_password} onChange={handlePasswordChange} required />
                                            </div>
                                            <button type="submit" className="btn btn-primary">Modifica Password</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}